/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import SEOLocaleList from '@apps/components/SEOLocaleList';
import SideAds from '@apps/components/SideAds';
import { BASE_URL } from '@apps/config';
import getNewsListing from '@apps/config/API/listing.config';
import { getNewCarById } from '@apps/config/API/new-car.config';
import NewsListingScreen from '@apps/screens/NewsListing';
import { localeConversions } from '@apps/utils/locale-conversion';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { startCase } from 'lodash';
import { NextPage, NextPageContext } from 'next';
import Error from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const Page: NextPage = (props) => {
  // ======================= HOOKS
  const { settings } = props;
  const router = useRouter();
  const { lang, t } = useTranslation('common');
  const newLang = localeConversions(lang);

  // ======================= STATE
  const carBrand = settings.car_brand_featured;
  const pageSize = 40;
  const [data, setData] = useState<any>();
  const [loading, setloading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cars, setCars] = useState();

  // ======================= API
  const fetchData = async (pageNumber: number) => {
    try {
      const brand = (router.query?.brand as string) ?? '';

      setloading(true);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth' as any,
        });
      }, 0);

      const res = await getNewsListing({
        pageSize,
        page: pageNumber,
        locale: newLang,
        brand,
      });
      const newEvCar = await getNewCarById({ id: 4 });
      const newCars = newEvCar?.data?.find((car: any) => {
        return car.new_car_feature_id === 4;
      });
      if (res.data.length < 1) {
        return <Error statusCode={404} />;
      }
      setCars(newCars);

      setData(res);
      setloading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  useEffect(() => {
    const { page } = router.query;
    setCurrentPage(parseInt(page as string) || 1);
    fetchData(parseInt(page as string) || 1);
  }, [router?.asPath, router?.locale]);

  const news = data;

  // ======================= VARIABLES
  const currentPath = router?.query?.brand;
  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: t('news'),
      url: '/',
    },
    {
      title: startCase(currentPath as string),
    },
  ];

  const brandName = startCase(currentPath as string);
  const seoTitle = `Latest ${brandName} Cars News, Updates & Launches | Carz Automedia Malaysia`;
  const seoDescription = `Discover the latest news and updates about ${brandName} cars in Malaysia. Stay informed on new models, features, and exciting developments. Whether you're a fan or a potential buyer, get all the latest information about ${brandName} right here at Carz.com.my.`;
  const currentUrl =
    lang === 'en'
      ? `${BASE_URL}${router?.asPath}`
      : `${BASE_URL}/${lang}${router?.asPath}`;
  const cleanedPath = currentUrl.split('?')[0].toLowerCase();

  // ======================= VIEWS
  return (
    <>
      <Head>
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:url" content={cleanedPath} />
      </Head>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        titleTemplate={seoTitle}
        canonical={cleanedPath}
      />
      <SEOLocaleList />
      <SideAds
        adsKeyLeft={'news_detail_vertical_left'}
        adsKeyRight={'news_detail_vertical_right'}
      />
      <div>
        <Breadcrumb options={breadcrumbOpt} />
        <h1 className="text-3xl">
          {t('latestResult', { keyword: startCase(currentPath as string) })}
        </h1>
        <NewsListingScreen
          categorySlug={currentPath}
          data={news?.data}
          currentPage={currentPage}
          totalCount={news?.meta?.total ?? 0}
          pageSize={pageSize}
          carBrand={carBrand}
          newEvCar={cars}
          loading={loading}
          href={`/latest/${currentPath}`}
        />
      </div>
    </>
  );
};

export async function getServerSideProps(ctx: NextPageContext) {
  const locale = ctx?.locale ?? 'en';
  const newLocale = localeConversions(locale);
  const brand = (ctx.query?.brand as string) ?? '';
  const data = await getNewsListing({
    pageSize: 40,
    page: 1,
    locale: newLocale,
    brand,
  });
  const hasData = data?.data?.length > 0 ?? false;
  if (!hasData) {
    return { notFound: true };
  }
  return { props: {} };
}

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default Page;
